import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getMySubscription } from "../services/subscriptionsApi";
import { useAuth } from "./AuthContext";

const SubscriptionContext = createContext(null);

const EXPIRY_WARN_DAYS = 7;
const EXPIRY_URGENT_DAYS = 3;
// Yearly plans are a 790–3,990 ₼ B2B decision with NO auto-charge behind them (the backend
// never re-bills a yearly term) — a 7-day heads-up is too tight for a purchase that may need
// a director's sign-off. Warn a month out, turn urgent a week out.
const YEARLY_EXPIRY_WARN_DAYS = 30;
const YEARLY_EXPIRY_URGENT_DAYS = 7;

const daysBetween = (todayIso, endDateIso) => {
  if (!endDateIso) return null;
  const today = new Date(todayIso);
  const end = new Date(endDateIso);
  return Math.round((end.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  // Subscription is owner/admin-only (matches the backend privileges + routes). Don't fetch it for
  // other roles — they can't view or act on it, and the endpoint would 403 on every page load.
  const canViewSubscription = !!user?.roles?.some((r) => r === "ADMIN" || r === "OWNER");
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !canViewSubscription) {
      setSubscription(null);
      return;
    }
    setLoading(true);
    try {
      const data = await getMySubscription();
      setSubscription(data);
      setError(null);
    } catch (e) {
      // 402 from hard-gate filter still returns JSON; let interceptor handle the redirect.
      // Other errors (network, 5xx) — keep last subscription, surface error.
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, canViewSubscription]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => {
    const status = subscription?.status;
    // For a YEARLY plan, endDate is only the CURRENT MONTHLY CYCLE (limits refill on it and
    // the sweep rolls it for free) — counting down to it would raise a false "expiring" banner
    // every single month. What actually runs out is the prepaid term, paidThrough.
    const isYearly = subscription?.billingType === "YEARLY";
    const expiryDate = isYearly
      ? (subscription?.paidThrough || subscription?.endDate)
      : subscription?.endDate;
    const todayIso = new Date().toISOString().slice(0, 10);
    const daysUntilExpiry = daysBetween(todayIso, expiryDate);

    const isExpired = status === "EXPIRED";
    const hasFailedPayment = status === "PAYMENT_FAILED";
    // Auto-renewing subscriptions are NOT expiring — the scheduler charges the saved card and
    // extends the term, so "X days left, renew now" is both wrong and alarming. If that charge
    // fails the status becomes PAYMENT_FAILED, which raises the banner again on its own, so
    // suppressing the countdown here loses no warning that actually matters.
    // Yearly plans are the opposite: nothing is ever auto-charged at the end of the term
    // (it just expires), so their countdown is meaningful regardless of the flag.
    const warnDays = isYearly ? YEARLY_EXPIRY_WARN_DAYS : EXPIRY_WARN_DAYS;
    const urgentDays = isYearly ? YEARLY_EXPIRY_URGENT_DAYS : EXPIRY_URGENT_DAYS;
    const isExpiringSoon =
      (status === "TRIAL" || status === "ACTIVE") &&
      (isYearly || subscription?.autoRenew !== true) &&
      daysUntilExpiry !== null &&
      daysUntilExpiry <= warnDays;
    const isUrgent =
      hasFailedPayment ||
      (isExpiringSoon && daysUntilExpiry <= urgentDays);

    return {
      subscription,
      loading,
      error,
      refresh,
      daysUntilExpiry,
      isExpired,
      isExpiringSoon,
      isUrgent,
      hasFailedPayment,
    };
  }, [subscription, loading, error, refresh]);

  return (
    <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (ctx === null) {
    // When used outside the provider (e.g. on the public callback page), return safe defaults
    // so callers don't have to null-check.
    return {
      subscription: null,
      loading: false,
      error: null,
      refresh: async () => {},
      daysUntilExpiry: null,
      isExpired: false,
      isExpiringSoon: false,
      isUrgent: false,
      hasFailedPayment: false,
    };
  }
  return ctx;
};
